import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { apiFetch } from './apiClient';
import { emitInternalNavigation } from './internalNavigate';
import { playAlertSound } from './notifications';

let listenersAttached = false;
let lastRegisteredToken = null;
let initInFlight = null;

const log = (...args) => console.warn('[Push]', ...args);

function isNativePushSupported() {
  return Capacitor.isNativePlatform();
}

async function getPushPlugin() {
  const mod = await import('@capacitor/push-notifications');
  return mod.PushNotifications;
}

/** Wait until the app is in the foreground (avoids swallowing iOS permission UI mid–OAuth). */
async function waitUntilAppActive(timeoutMs = 8000) {
  if (!isNativePushSupported()) return true;
  try {
    const state = await CapApp.getState();
    if (state?.isActive) return true;
  } catch (_) {
    return true;
  }

  return new Promise((resolve) => {
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      try {
        listener?.remove?.();
      } catch (_) {
        /* ignore */
      }
      resolve(ok);
    };
    let listener;
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) finish(true);
    }).then((handle) => {
      listener = handle;
    }).catch(() => finish(true));
    setTimeout(() => finish(true), timeoutMs);
  });
}

async function registerTokenWithApi(token) {
  if (!token || token === lastRegisteredToken) {
    log('skip API register', { hasToken: !!token, alreadySent: token === lastRegisteredToken });
    return false;
  }
  const platform = Capacitor.getPlatform();
  if (platform !== 'ios' && platform !== 'android') return false;
  try {
    log('POST /api/devices…', { platform, tokenLen: token.length });
    const res = await apiFetch('/api/devices', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, platform }),
    });
    const body = await res.json().catch(() => ({}));
    if (res.ok) {
      lastRegisteredToken = token;
      log('device registered', { push_configured: body?.push_configured, status: res.status });
      return true;
    }
    log('Device token register failed:', res.status, body);
    return false;
  } catch (e) {
    log('Device token register error:', e?.message || e);
    return false;
  }
}

function pathFromNotificationData(data) {
  if (!data || typeof data !== 'object') return null;
  if (typeof data.path === 'string' && data.path.startsWith('/')) return data.path;
  if (typeof data.order_id === 'string' && data.order_id) {
    if (String(data.type || '').startsWith('order_')) {
      return `/orders/${data.order_id}`;
    }
  }
  if (data.type === 'new_order' || String(data.type || '').startsWith('nudge_')) {
    return '/dashboard';
  }
  return null;
}

async function ensureListeners(PushNotifications) {
  if (listenersAttached) return;
  listenersAttached = true;
  log('attaching push listeners');
  await PushNotifications.addListener('registration', (token) => {
    log('registration event', { tokenLen: token?.value?.length });
    registerTokenWithApi(token?.value);
  });
  await PushNotifications.addListener('registrationError', (err) => {
    log('registrationError', err);
  });
  await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
    const path = pathFromNotificationData(event?.notification?.data);
    if (path) emitInternalNavigation(path);
  });
  await PushNotifications.addListener('pushNotificationReceived', (event) => {
    const data = event?.notification?.data || {};
    const type = String(data.type || '');
    if (
      type === 'new_order'
      || type.startsWith('nudge_')
      || type === 'unapproved30'
      || type === 'notready15'
    ) {
      playAlertSound();
    }
  });
}

/**
 * Request permission, register for remote push, and wire tap → in-app navigate.
 * Safe to call multiple times; no-ops on web.
 * @param {{ interactive?: boolean }} opts interactive=true shows the system prompt if needed
 * @returns {Promise<{ ok: boolean, reason?: string, permission?: string }>}
 */
export async function initRemotePush(opts = {}) {
  const interactive = opts.interactive !== false;
  if (!isNativePushSupported()) {
    log('skip: not native');
    return { ok: false, reason: 'not_native' };
  }
  if (initInFlight) {
    log('reusing in-flight init');
    return initInFlight;
  }

  initInFlight = (async () => {
    try {
      log('init start', { interactive, platform: Capacitor.getPlatform() });
      await waitUntilAppActive();
      // Brief settle after OAuth / navigation so iOS can present the permission alert.
      await new Promise((r) => setTimeout(r, interactive ? 600 : 200));

      const PushNotifications = await getPushPlugin();
      let perm = await PushNotifications.checkPermissions();
      log('checkPermissions', perm);

      if (perm.receive !== 'granted') {
        if (!interactive) {
          log('not granted; skipping prompt (non-interactive)');
          return { ok: false, reason: 'needs_permission', permission: perm.receive };
        }
        log('requestPermissions…');
        perm = await PushNotifications.requestPermissions();
        log('requestPermissions result', perm);
      }

      if (perm.receive !== 'granted') {
        log('permission not granted', perm.receive);
        return { ok: false, reason: 'denied', permission: perm.receive };
      }

      await ensureListeners(PushNotifications);
      log('calling register()');
      await PushNotifications.register();
      log('register() returned (token arrives via registration event)');
      return { ok: true, permission: perm.receive };
    } catch (e) {
      log('initRemotePush failed:', e?.message || e);
      return { ok: false, reason: 'error', error: String(e?.message || e) };
    } finally {
      initInFlight = null;
    }
  })();

  return initInFlight;
}

/** Check current native push permission without prompting. */
export async function getPushPermissionStatus() {
  if (!isNativePushSupported()) return 'unavailable';
  try {
    const PushNotifications = await getPushPlugin();
    const perm = await PushNotifications.checkPermissions();
    return perm?.receive || 'prompt';
  } catch (e) {
    log('getPushPermissionStatus failed', e?.message || e);
    return 'unavailable';
  }
}

/** Remove this device token from the API (call before clearing session on logout). */
export async function unregisterRemotePush() {
  if (!isNativePushSupported()) return;
  const token = lastRegisteredToken;
  lastRegisteredToken = null;
  try {
    if (token) {
      await apiFetch('/api/devices', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
    } else {
      await apiFetch('/api/devices', { method: 'DELETE' });
    }
  } catch (e) {
    log('unregisterRemotePush failed:', e?.message || e);
  }
}
